import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useEcgDetailsContext } from "../../../context/EcgDetailsContext";
import Card from "../../Shared/Card/Card";
import { CheckBox } from "@shltelemedicine/shl-ui-lib";
import CircleIndicator from "../../Shared/CircleIndicator/CircleIndicator";
import InterpretationIcon from "../../Shared/InterpretationIcon/InterpretationIcon";
import Tabs, { Tab } from "../../Shared/Tabs/Tabs";
import ToggleSwitch from "../../Shared/ToggleSwitch/ToggleSwitch";
import UrgentIcon from "../../Shared/UrgentIcon/UrgentIcon";
import Actions from "./Actions/Actions";
import Findings from "./Findings/Findings";
import Interpretations from "./Interpretations/Interpretations";
import styles from "./EcgEdit.module.css";
import useResponsive from "../../../hooks/useResponsive";
import { useSplitContainerContext } from "../../../context/SplitContainerContext";
import { isIphone } from "../../../utils/envUtil";

enum TabKeys {
  findings = "findings",
  interpretations = "interpretations",
  // notes = "notes",
  urgent = "urgent",
  readable = "readable",
  actions = "actions",
}

const getDefaultTab = (
  findingsEnabled?: boolean,
  interpretationEnabled?: boolean
) =>
  findingsEnabled
    ? TabKeys.findings
    : interpretationEnabled
    ? TabKeys.interpretations
    : undefined;

type EcgEditProps = {
  isCollapsed?: boolean;
  showLeadsPane: (findingKey: string, findingName: string) => void;
  setPaneState?: () => void;
};

const EcgEdit = ({
  isCollapsed,
  showLeadsPane,
  setPaneState,
}: EcgEditProps) => {
  const {
    state: { loading, selectedEcg, ecgEdit },
    actions: { edit },
  } = useEcgDetailsContext();
  const {
    actions: { togglePane },
  } = useSplitContainerContext();

  const defaultTab = getDefaultTab(
    selectedEcg?.permissions?.enableFindings,
    selectedEcg?.permissions?.enableInterpretation
  );

  const { isMobile, isTabletPortrait } = useResponsive();

  const isCollapsedOnMobile = isMobile && isCollapsed;

  const [selectedTab, setSelectedTab] = useState<TabKeys | undefined>(
    defaultTab
  );

  const BOTTOM_PANE_ID = "1";

  const [hoveredTab, setHoveredTab] = useState<TabKeys | undefined>(defaultTab);

  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [selectedEcg?.id]); // eslint-disable-line

  const onToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!ecgEdit) return;
    const id = event.currentTarget.name;

    if (id === TabKeys.urgent) {
      ecgEdit.isUrgent = !ecgEdit.isUrgent;
    } else if (id === TabKeys.readable) {
      ecgEdit.isReadable = !ecgEdit.isReadable;
    }

    edit(ecgEdit);
  };

  const onTabChangeHandler = (id: string | null) => {
    if (!id) return;
    setSelectedTab(id as TabKeys);
    if (isCollapsed) {
      togglePane(BOTTOM_PANE_ID);
      if (setPaneState) setPaneState();
    }
  };

  const tabHoverHandler = (tabId: string) => {
    if (tabId === selectedTab) return;
    //on iPhones first tap on tab title causes hover effect and does not switch to the chosen tap, so for iPhone we need to call setSelectedTab without the setHoveredTab
    isIphone()
      ? setSelectedTab(tabId as TabKeys)
      : setHoveredTab(tabId as TabKeys);
  };
  const tabHoverEndHandler = () => {
    setHoveredTab(selectedTab);
  };

  const disabled = loading || !selectedEcg || ecgEdit?.isReadable === false;

  let tabs: Tab[] = [];

  if (selectedEcg?.permissions?.enableFindings) {
    tabs.push(
      // Findings
      {
        id: TabKeys.findings,
        title: (
          <>
            <p className={styles.findingsTabContainer}>
              <CircleIndicator
                className={
                  selectedTab === TabKeys.findings ||
                  hoveredTab === TabKeys.findings
                    ? styles.findingsIconActive
                    : styles.findingsIconInactive
                }
              />{" "}
              <span className="mx-sm">
                {selectedEcg?.permissions.findingsRequired && <>*</>}
              </span>
              <FormattedMessage
                id="ECG_DETAILS.FINDINGS"
                defaultMessage="Findings"
              />
            </p>
          </>
        ),
        body: !isCollapsedOnMobile && (
          <Findings showLeadsPane={showLeadsPane} />
        ),
        bodyClassName: !isMobile ? "pl-3" : "pl-1",
        disabled: disabled,
      }
    );
  }

  if (selectedEcg?.permissions?.enableInterpretation) {
    tabs.push(
      // Interpretations
      {
        id: TabKeys.interpretations,
        title: (
          <>
            <InterpretationIcon
              disabled={!selectedEcg || loading}
              selected={selectedTab === TabKeys.interpretations}
              hovered={hoveredTab === TabKeys.interpretations}
            />
            <span className="mx-sm">
              {selectedEcg?.permissions.interpretationRequired && <>*</>}
            </span>
            <FormattedMessage
              id="ECG_DETAILS.INTERPRETATION"
              defaultMessage="Interpretation"
            />
          </>
        ),
        body: !isCollapsedOnMobile && (
          <div className="h-100 d-flex flex-column">
            <div className="flex-grow-1">
              <Interpretations />
            </div>
          </div>
        ),
        bodyClassName: `${!isMobile && "pl-3"} h-100`,
        disabled: disabled,
      }
    );
  }

  if (selectedEcg?.permissions?.enableActions) {
    tabs.push(
      //Action required
      {
        id: TabKeys.actions,
        title: (
          <>
            <span className="mx-sm">
              {selectedEcg?.permissions.interpretationRequired && <>*</>}
            </span>
            <FormattedMessage
              id="ECG_DETAILS.ACTION_REQUIRED"
              defaultMessage="Action Required"
            />
          </>
        ),
        body: !isCollapsedOnMobile && (
          <div className="h-100 d-flex flex-column">
            <div className="flex-grow-1">
              <Actions />
            </div>
          </div>
        ),
        bodyClassName: `${!isMobile && "pl-3"} h-100`,
        disabled: disabled,
      }
    );
  }

  if (
    selectedEcg?.permissions?.enableFindings ||
    selectedEcg?.permissions?.enablePriority
  ) {
    tabs.push(
      // Side controls
      {
        titleClassName: `d-flex align-items-center mx-1 mx-sm-0 ${(selectedEcg
          ?.permissions?.enableFindings ||
          selectedEcg?.permissions?.enableInterpretation) &&
          "ml-sm-auto"} ${isMobile && styles.titleClassMobile}`,
        customTitle: (
          <>
            {selectedEcg?.permissions?.enablePriority && (
              <div
                className={
                  isTabletPortrait && !isMobile
                    ? "d-none"
                    : `${isMobile && styles.sideControlsMobile}  d-flex p-sm-2`
                }
              >
                <UrgentIcon />
                <CheckBox
                  name={TabKeys.urgent}
                  label={
                    <FormattedMessage
                      id="ECG_DETAILS.FLAG_URGENT"
                      defaultMessage="Flag as Priority"
                    />
                  }
                  checked={ecgEdit?.isUrgent}
                  onChange={onToggle}
                  disabled={disabled}
                />
              </div>
            )}

            {selectedEcg?.permissions?.enableFindings && (
              <>
                <div className={`${styles.divider}`}></div>
                <div
                  className={` ${
                    isMobile ? styles.sideControlsMobile : ""
                  } d-sm-flex align-items-center p-2`}
                >
                  <p className="mb-0 user-select-none mx-md-0 mx-2">
                    <FormattedMessage
                      id="ECG_DETAILS.READABLE_ECG"
                      defaultMessage="Readable ECG"
                    />
                  </p>
                  <ToggleSwitch
                    name={TabKeys.readable}
                    offLabel={
                      <FormattedMessage id="GLOBAL.NO" defaultMessage="NO" />
                    }
                    onLabel={
                      <FormattedMessage id="GLOBAL.YES" defaultMessage="YES" />
                    }
                    on={ecgEdit?.isReadable}
                    onChange={onToggle}
                    disabled={loading || !selectedEcg}
                  />
                </div>
              </>
            )}
          </>
        ),
        disabled: loading || !selectedEcg,
      }
    );
  }

  return (
    <div className="h-100 w-100 d-flex flex-column position-relative">
      {ecgEdit?.isReadable === false && (
        <Card className="prim-bg-color-dark absolute-center mt-4 z-index-front w-50 text-center">
          <span>
            <FormattedMessage
              id="ECG_DETAILS.MARKED_AS_UNREADABLE"
              defaultMessage="Marked as Unreadable"
            />
          </span>
        </Card>
      )}
      {tabs.length > 0 && (
        <Tabs
          disabled={!selectedEcg}
          defaultActiveKey={defaultTab}
          activeKey={selectedTab}
          tabs={tabs}
          onTabHover={tabHoverHandler}
          onTabHoverEnd={tabHoverEndHandler}
          onSelect={onTabChangeHandler}
          navBarClassName={`${
            isMobile ? `${styles.tabsMobile} pt-4` : "pl-3 pt-4 pt-lg-3"
          }`}
          contentClassName={`${isCollapsedOnMobile &&
            styles.collapseMobile} flex-grow-1 overflow-auto`}
        />
      )}
    </div>
  );
};

export default EcgEdit;
